<template>
  <div class="range-picker-advanced">
    <popover :open.sync="dropdown" :disabled="disabled" :should-close="shouldClose" fixed
      placement="bottom-end">
      <button slot="trigger" :class="{ button: true, 'is-small': small, 'is-static': disabled }"
        type="button">
        <octicon :icon="calendar" />
        <span>{{ text }}</span>
      </button>
      <tabs :show.sync="active" :theme="$_ui_theme_tabs">
        <tab :title="$t('picker.presets')">
          <div class="columns">
            <div v-for="(chunk, index) in suggestionsChunks" :key="index" class="column">
              <a v-for="s in chunk" :key="s.key" :class="{
                'dropdown-item': true,
                'is-active': suggestion && s.key === suggestion.key
              }" href="#" @click.prevent="choose(s)">{{ $t('picker.' + s.key) }}</a>
            </div>
          </div>
        </tab>
        <tab :title="$t(enableTime ? 'picker.range' : 'picker.rangeDateOnly')">
          <input ref="rp" v-rangepicker="pickerBinding" type="text" class="input">
        </tab>
      </tabs>
    </popover>
  </div>
</template>
<script>
import { chunk } from '@/utils'
import { Tab, Tabs } from '@cyradar/ui'
import {
  startOfDay,
  startOfMonth,
  startOfWeek,
  startOfYear,
  endOfMonth,
  subDays,
  subMonths,
  subMinutes,
  subHours
} from 'date-fns'
import Flatpickr from 'flatpickr'
import { calendar } from 'octicons-vue'

export default {
  i18n: {
    dateTimeFormats: {
      en: {
        short: {
          year: 'numeric',
          month: 'numeric',
          day: 'numeric',
          hour: 'numeric',
          minute: 'numeric',
          hour12: false
        },
        dateOnly: {
          year: 'numeric',
          month: 'numeric',
          day: 'numeric'
        }
      },
      vi: {
        short: {
          year: 'numeric',
          month: 'numeric',
          day: 'numeric',
          hour: 'numeric',
          minute: 'numeric',
          hour12: false
        },
        dateOnly: {
          year: 'numeric',
          month: 'numeric',
          day: 'numeric'
        }
      }
    },
    messages: {
      en: {
        picker: {
          pick: 'Pick a range',
          presets: 'Presets',
          range: 'Date & Time Picker',
          rangeDateOnly: 'Date Picker',
          day0: 'Today',
          week0: 'This week',
          month0: 'This month',
          year0: 'This year',
          'day-1': 'Last 24h',
          'day-7': 'Last 7 days',
          'day-30': 'Last 30 days',
          'month-1': 'Last month',
          'month-3': 'Last 3 months',
          'minute-5': 'Last 5 minutes',
          'minute-15': 'Last 15 minutes',
          'minute-30': 'Last 30 minutes',
          'hour-1': 'Last hour',
          'hour-4': 'Last 4 hours',
          'hour-12': 'Last 12 hours',
          'hour-24': 'Last 24 hours'
        }
      },
      vi: {
        picker: {
          pick: 'Khoảng thời gian',
          presets: 'Gợi ý',
          range: 'Ngày và Giờ',
          rangeDateOnly: 'Chọn ngày',
          day0: 'Hôm nay',
          week0: 'Tuần này',
          month0: 'Tháng này',
          year0: 'Năm này',
          'day-1': '24 giờ qua',
          'day-7': '7 ngày qua',
          'day-30': '30 ngày qua',
          'month-1': 'Tháng trước',
          'month-3': '3 tháng qua',
          'minute-5': '5 phút qua',
          'minute-15': '15 phút qua',
          'minute-30': '30 phút qua',
          'hour-1': '1 giờ qua',
          'hour-4': '4 giờ qua',
          'hour-12': '12 giờ qua',
          'hour-24': '24 giờ qua'
        }
      }
    }
  },
  components: { Tabs, Tab },
  directives: {
    rangepicker: {
      bind(el, binding, vnode) {
        const options = binding.value || {}
        el.picker = new Flatpickr(el, {
          defaultDate: options.dates,
          mode: 'range',
          enableTime: options.enableTime,
          time_24hr: true,
          dateFormat: options.enableTime ? 'Y-m-d H:i' : 'Y-m-d',
          onChange(val) {
            if (!val || val.length !== 2) {
              return
            }

            vnode.context.suggestion = null
            vnode.context.dates = [val[0], val[1]]
          }
        })
      },
      update(el, binding) {
        if (!el.picker) {
          return
        }

        const options = binding.value || {}
        el.picker.set('enableTime', options.enableTime)
        el.picker.set('dateFormat', options.enableTime ? 'Y-m-d H:i' : 'Y-m-d')
        el.picker.setDate(options.dates || [], false)
      },
      unbind(el) {
        if (!el.picker) {
          return
        }

        el.picker.destroy()
      }
    }
  },
  props: {
    lookup: {
      type: String,
      default: ''
    },
    range: {
      type: Array,
      default() {
        return []
      }
    },
    disabled: {
      type: Boolean,
      default: false
    },
    allowedPresets: {
      type: Array,
      default() {
        return []
      }
    },
    small: {
      type: Boolean,
      default: false
    },
    enableTime: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      active: 0,
      dropdown: false,
      suggestion: null,
      dates: null,
      now: new Date()
    }
  },
  computed: {
    text() {
      if (this.type === 'presets') {
        return this.$t(`picker.${this.suggestion.key}`)
      }

      if (this.type === 'range') {
        const formatKey = this.enableTime ? 'short' : 'dateOnly'
        return (
          this.$d(this.dates[0], formatKey) +
          ' - ' +
          this.$d(this.dates[1], formatKey)
        )
      }

      return this.$t('picker.pick')
    },
    type() {
      if (this.suggestion && this.suggestion.key) {
        return 'presets'
      }

      if (this.dates && this.dates.length === 2) {
        return 'range'
      }

      return ''
    },
    calendar() {
      return calendar
    },
    pickerBinding() {
      return {
        dates: this.dates,
        enableTime: this.enableTime
      }
    },
    suggestions() {
      const today = this.now
      const suggestions = [
        { key: 'day0', value: [startOfDay(today), today] },
        { key: 'week0', value: [startOfWeek(today, { weekStartsOn: 1 }), today] },
        { key: 'month0', value: [startOfMonth(today), today] },
        { key: 'year0', value: [startOfYear(today), today] },
        { key: 'day-1', value: [subDays(today, 1), today] },
        { key: 'day-7', value: [subDays(today, 7), today] },
        { key: 'day-30', value: [subDays(today, 30), today] },
        { key: 'month-1', value: [startOfMonth(subMonths(today, 1)), endOfMonth(subMonths(today, 1))] },
        { key: 'month-3', value: [subMonths(today, 3), today] },
        { key: 'minute-5', value: [subMinutes(today, 5), today] },
        { key: 'minute-15', value: [subMinutes(today, 15), today] },
        { key: 'minute-30', value: [subMinutes(today, 30), today] },
        { key: 'hour-1', value: [subHours(today, 1), today] },
        { key: 'hour-4', value: [subHours(today, 4), today] },
        { key: 'hour-12', value: [subHours(today, 12), today] },
        { key: 'hour-24', value: [subHours(today, 24), today] }
      ]

      if (!this.allowedPresets || this.allowedPresets.length === 0) {
        return suggestions
      }

      return suggestions.filter((suggestion) => this.allowedPresets.includes(suggestion.key))
    },
    suggestionsChunks() {
      return chunk(this.suggestions, 6)
    },
    containsStrings() {
      if (!this.range || !this.range.length) {
        return false
      }

      return typeof this.range[0] === 'string'
    }
  },
  watch: {
    lookup: {
      immediate: true,
      handler: 'setSuggestionFromLookup'
    },
    range: {
      immediate: true,
      handler(val) {
        if (!val) {
          return
        }

        if (!this.containsStrings) {
          this.dates = val
          return
        }

        this.dates = val.map((x) => new Date(x))
      }
    },
    suggestion: {
      immediate: true,
      handler(val) {
        if (!val) {
          this.$emit('update:lookup', '')
          return
        }

        this.$emit('update:lookup', val.key)
        this.dates = val.value
      }
    },
    dates: {
      immediate: true,
      handler(val) {
        this.$emit('update:range', val)
      }
    }
  },
  methods: {
    reloadSuggestions() {
      this.now = new Date()
      this.setSuggestionFromLookup()
    },
    setSuggestionFromLookup() {
      const item = this.suggestions.find(
        (suggestion) => suggestion.key === this.lookup
      )
      if (!item) {
        this.suggestion = null
        return
      }

      this.suggestion = item
    },
    choose(suggestion) {
      if (!suggestion) {
        return
      }

      this.reloadSuggestions()
      this.suggestion = this.suggestions.find((item) => item.key === suggestion.key) || suggestion
      this.dropdown = false
    },
    shouldClose(e) {
      if (this.$el.contains(e.target)) {
        return false
      }

      if (
        !this.$refs.rp ||
        !this.$refs.rp.picker ||
        !this.$refs.rp.picker.calendarContainer
      ) {
        return true
      }

      if (this.$refs.rp.picker.calendarContainer.contains(e.target)) {
        return false
      }

      if (e.target.tagName === 'BODY' && this.$refs.rp.picker.isOpen) {
        // sometimes clicking into the input fires events whose target is body
        return false
      }

      return true
    }
  }
}
</script>
<style lang="scss">
.range-picker-advanced {
  .dropdown-menu {
    min-width: 30rem;
  }
}
</style>
